import { UzButton, UzInput, UzTextarea } from '@utazzo/ui';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqBuilderProps {
  /** Serialized FAQ HTML (stored as the post's faqHtml). */
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
}

const FAQ_FORMAT = 'utazzo-blog-v2';

/** Strip HTML to plain text (answers/questions are pasted rich text). */
const toText = (html: string): string => {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

interface ParsedFaq {
  items: FaqItem[];
  /** Optional intro blurb the portal stores alongside the Q&A list. */
  faqContent: string;
}

/**
 * Parse the stored `faqHtml`. The portal/website format is a JSON blob
 * `{ format:"utazzo-blog-v2", faqContent, iFAQDetails:[{pQuestion,pAnswer}] }`
 * (pAnswer is rich text). We also accept our earlier `.faq-item` HTML as a
 * fallback so nothing breaks. Answers are flattened to plain text for the
 * textarea editor.
 */
const parse = (raw: string): ParsedFaq => {
  const out: ParsedFaq = { items: [], faqContent: '' };
  if (!raw) return out;
  const trimmed = raw.trim();

  // 1) Portal JSON format.
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed) as {
        faqContent?: unknown;
        iFAQDetails?: Array<{ pQuestion?: unknown; pAnswer?: unknown }>;
      };
      if (Array.isArray(obj.iFAQDetails)) {
        out.faqContent = typeof obj.faqContent === 'string' ? obj.faqContent : '';
        out.items = obj.iFAQDetails.map((d) => ({
          question: toText(String(d.pQuestion ?? '')),
          answer: toText(String(d.pAnswer ?? '')),
        }));
        return out;
      }
    } catch {
      /* not JSON - fall through to the HTML parser */
    }
  }

  // 2) Legacy `.faq-item` HTML (our earlier serialization).
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    const blocks = Array.from(doc.querySelectorAll('.faq-item'));
    if (blocks.length) {
      out.items = blocks.map((el) => ({
        question: toText(el.querySelector('.faq-q')?.innerHTML ?? ''),
        answer: toText(el.querySelector('.faq-a')?.innerHTML ?? ''),
      }));
    }
  } catch {
    /* leave items empty */
  }
  return out;
};

/**
 * Serialize Q&A pairs back into the portal's `utazzo-blog-v2` JSON so the
 * website (which reads `iFAQDetails`) keeps working. `faqContent` is preserved.
 */
const serialize = (items: FaqItem[], faqContent: string): string => {
  const clean = items.filter((i) => i.question.trim() || i.answer.trim());
  if (clean.length === 0 && !faqContent.trim()) return '';
  return JSON.stringify({
    format: FAQ_FORMAT,
    faqContent,
    iFAQDetails: clean.map((i) => ({ pQuestion: i.question.trim(), pAnswer: i.answer.trim() })),
  });
};

/**
 * Structured FAQ editor: a list of Question + Answer inputs ("FAQ 1", "FAQ 2",
 * …) with add/remove. Emits the serialized HTML upward so it persists in the
 * existing `faqHtml` field - no schema change required.
 */
export const FaqBuilder = ({ value, onChange, disabled }: FaqBuilderProps): React.JSX.Element => {
  const initial = parse(value);
  const [items, setItems] = useState<FaqItem[]>(initial.items);
  // Index of the single expanded FAQ (single-open accordion); null = all closed.
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqContentRef = useRef(initial.faqContent);
  const lastEmitted = useRef(value);

  // Re-parse only on external changes (e.g. the edit form loading a post),
  // never on our own emits - otherwise typing would reset the inputs.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      const parsed = parse(value);
      setItems(parsed.items);
      faqContentRef.current = parsed.faqContent;
      lastEmitted.current = value;
      setOpenIndex(null);
    }
  }, [value]);

  const commit = (next: FaqItem[]): void => {
    setItems(next);
    const serialized = serialize(next, faqContentRef.current);
    lastEmitted.current = serialized;
    onChange(serialized);
  };

  const update = (index: number, patch: Partial<FaqItem>): void =>
    commit(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));

  const add = (): void => {
    commit([...items, { question: '', answer: '' }]);
    setOpenIndex(items.length); // auto-open the freshly added FAQ
  };

  const remove = (index: number): void => {
    commit(items.filter((_, i) => i !== index));
    // Keep the open state pointing at the right row after removal.
    setOpenIndex((cur) => {
      if (cur === null) return null;
      if (cur === index) return null;
      return cur > index ? cur - 1 : cur;
    });
  };

  const toggle = (index: number): void => setOpenIndex((cur) => (cur === index ? null : index));

  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="text-sm text-(--fg-faint)">No FAQs yet. Add a question and answer below.</p>
      )}

      {items.map((item, i) => {
        const open = openIndex === i;
        const heading = item.question.trim() || `FAQ ${i + 1}`;
        return (
          <div key={i} className="border-input overflow-hidden rounded-lg border bg-(--z-50)">
            {/* Collapsed header: chevron + question text + remove. */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggle(i)}
                aria-expanded={open}
                aria-label={open ? `Collapse FAQ ${i + 1}` : `Expand FAQ ${i + 1}`}
                className="flex min-w-0 flex-1 items-center gap-2.5 px-4 py-3 text-left"
              >
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-(--fg-faint) transition-transform ${
                    open ? 'rotate-180' : ''
                  }`}
                />
                <span className="shrink-0 text-xs font-semibold text-(--fg-muted)">
                  FAQ {i + 1}
                </span>
                <span
                  className={`min-w-0 flex-1 truncate text-sm ${
                    item.question.trim() ? 'text-(--fg)' : 'text-(--fg-faint) italic'
                  }`}
                >
                  {heading}
                </span>
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={disabled}
                aria-label={`Remove FAQ ${i + 1}`}
                className="mr-2 rounded-md p-1.5 text-(--fg-faint) hover:bg-(--accent) hover:text-(--ds-error) disabled:opacity-40"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {/* Expanded body: Question + Answer editors. */}
            {open && (
              <div className="border-input space-y-3 border-t px-4 py-4">
                <UzInput
                  label="Question"
                  value={item.question}
                  placeholder="e.g. Is Kashmir safe to visit?"
                  disabled={disabled}
                  onChange={(e) => update(i, { question: e.target.value })}
                />
                <UzTextarea
                  label="Answer"
                  value={item.answer}
                  placeholder="Write the answer…"
                  rows={3}
                  disabled={disabled}
                  onChange={(e) => update(i, { answer: e.target.value })}
                />
              </div>
            )}
          </div>
        );
      })}

      <UzButton type="button" variant="secondary" size="sm" onClick={add} disabled={disabled}>
        <Plus className="mr-1.5 h-4 w-4" />
        Add FAQ
      </UzButton>
    </div>
  );
};
